import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDate, type CompanyCheck, type Paginated } from '@codepass/shared';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { customerPath } from '@/lib/paths';
import { Alert, Badge, Button, Card, Input, Skeleton, Table, Td, Th, Tr } from '@/components/ui';
import {
  EmptyState,
  Money,
  PageHeader,
  Pagination,
  PaymentTerms,
  RefreshButton,
} from '@/components/domain';
import { CompanyCheckDialog, CompanyRatingButton } from '@/components/CompanyCheck';
import { useCompanyChecks } from '@/lib/company-checks';
import type { CustomerListItem } from '@/types';

const LIMIT = 50;

export function CustomersPage() {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  /**
   * "Uden serviceaftale": kun kunder, der ikke har en aktiv serviceaftale - overblikket over, hvem
   * der kan tilbydes en. Slaaet fra som standard. Filtreret paa serveren, foer siderne deles op,
   * saa side 2 er de naeste kunder uden aftale og ikke et udsnit af alle kunders side 2.
   */
  const [withoutServiceAgreement, setWithoutServiceAgreement] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', search, offset, withoutServiceAgreement],
    queryFn: () =>
      api.get<Paginated<CustomerListItem>>('/customers', {
        search: search || undefined,
        without_service_agreement: withoutServiceAgreement || undefined,
        limit: LIMIT,
        offset,
      }),
  });

  /*
   * The company check in its own query: it looks up CVR, annual reports and the BC ledger per
   * customer, and the list must not wait for that. Status shows a placeholder until it arrives.
   */
  const checks = useCompanyChecks();
  const checkByCustomer = useMemo(
    () => new Map((checks.data?.data ?? []).map((check) => [check.customer_id, check])),
    [checks.data],
  );
  const [openCheck, setOpenCheck] = useState<CompanyCheck | null>(null);

  return (
    <>
      <PageHeader
        title="Kunder"
        description="Læst read-only fra Business Central. Kunder oprettes ikke i Codepass."
        actions={<RefreshButton prefixes={['customers']} />}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <Input
            className="max-w-sm"
            placeholder="Søg på navn eller CVR"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
          {/* Sin egen rettighed: 'Se alle kunder uden serviceaftale'. */}
          {can('customers.without_agreement') ? (
            <Button
              variant={withoutServiceAgreement ? 'primary' : 'secondary'}
              aria-pressed={withoutServiceAgreement}
              title="Vis kun kunder uden en aktiv serviceaftale i Dynamics"
              onClick={() => {
                setWithoutServiceAgreement((current) => !current);
                setOffset(0);
              }}
            >
              Uden serviceaftale
            </Button>
          ) : null}
        </div>

        {isLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : error ? (
          /*
           * Serverens egen besked frem for en generisk linje: naar kundelisten kommer live
           * fra Business Central, er det den der siger om det er samtykket, application
           * useren eller miljoeet der mangler. `message` er dansk og brugervendt - se
           * lib/errors.ts.
           */
          <Alert tone="error" className="m-4">
            {error instanceof ApiError ? error.message : 'Kunne ikke hente kunder.'}
          </Alert>
        ) : (data?.data.length ?? 0) === 0 ? (
          withoutServiceAgreement ? (
            <EmptyState
              title="Ingen kunder uden serviceaftale"
              description="Alle kunder, der matcher søgningen, har en aktiv serviceaftale."
            />
          ) : (
            <EmptyState title="Ingen kunder" description="Kunder læses fra Business Central." />
          )
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Kunde</Th>
                  <Th className="w-[110px]">CVR</Th>
                  <Th className="w-[150px] text-right">Aktive kontrakter</Th>
                  <Th className="w-[150px] text-right">Faktureret i år</Th>
                  <Th className="w-[130px]">Seneste faktura</Th>
                  <Th className="w-[150px]">Status</Th>
                </tr>
              </thead>
              <tbody>
                {data?.data.map((customer) => (
                  <Tr
                    key={customer.id}
                    className="cursor-pointer"
                    /*
                     * Hele raekken foerer til kunden. Linket paa navnet bliver staaende: det
                     * er raekkens tastaturvej og det der kan aabnes i en ny fane, og en
                     * `tr` kan ingen af de to ting. Klik paa linket bobler herop med
                     * defaultPrevented sat, og guarden nedenfor undgaar at samme side saa
                     * bliver lagt i historikken to gange.
                     */
                    onClick={(event) => {
                      if (event.defaultPrevented) return;
                      // Ctrl/cmd- og shift-klik hoerer til linket, som aabner en ny fane.
                      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                      navigate(customerPath(customer));
                    }}
                  >
                    <Td>
                      <Link to={customerPath(customer)} className="font-medium hover:underline">
                        {customer.name}
                      </Link>
                      {customer.archived ? (
                        <Badge tone="neutral" className="ml-2 align-middle">
                          Arkiveret
                        </Badge>
                      ) : null}
                      <span className="block text-xs text-ink-muted">
                        {customer.country_code !== 'DK' ? (
                          `${customer.country_code} · reverse charge`
                        ) : (
                          <PaymentTerms
                            days={customer.payment_terms_days}
                            issue={customer.payment_terms_issue}
                          />
                        )}
                      </span>
                    </Td>
                    <Td className="text-xs tabular">{customer.cvr ?? '—'}</Td>
                    <Td className="text-right tabular">{customer.active_contract_count}</Td>
                    <Td className="text-right">
                      <Money
                        value={customer.billed_ytd_excl_vat}
                        currency={customer.currency}
                        decimals={0}
                      />
                    </Td>
                    <Td className="text-xs tabular">
                      {formatDate(customer.last_invoice_at)}
                      {customer.days_since_last_invoice !== null ? (
                        <span className="block text-ink-muted">
                          {customer.days_since_last_invoice} dage siden
                        </span>
                      ) : null}
                    </Td>
                    <Td>
                      <CompanyCheckStatus
                        check={checkByCustomer.get(customer.id)}
                        loading={checks.isLoading}
                        failed={Boolean(checks.error)}
                        onOpen={setOpenCheck}
                      />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={data?.total ?? 0}
              limit={LIMIT}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>

      <CompanyCheckDialog check={openCheck} onClose={() => setOpenCheck(null)} />
    </>
  );
}

function CompanyCheckStatus({
  check,
  loading,
  failed,
  onOpen,
}: {
  check: CompanyCheck | undefined;
  loading: boolean;
  failed: boolean;
  onOpen: (check: CompanyCheck) => void;
}) {
  if (loading) return <Skeleton className="h-5 w-24" />;
  if (check) return <CompanyRatingButton check={check} onOpen={onOpen} />;
  return (
    <span
      className="text-xs text-ink-muted"
      title={failed ? 'Virksomhedstjekket kunne ikke hentes.' : 'Ingen vurdering af kunden.'}
    >
      —
    </span>
  );
}
